import {
	type InfiniteData,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import useActiveMerchantId from "src/hooks/use-merchant-id";
import balancesProvider from "src/providers/balance-provider";
import type {
	ConvertParams,
	ListWithdrawalsParams,
	MerchantBalance,
	WithdrawalsPagination,
} from "src/types";
import { sleep } from "src/utils";

const PAGE_SIZE = 50;

const requireMerchantId = (merchantId: string | null): string => {
	if (!merchantId) {
		throw new Error("Merchant ID is required");
	}
	return merchantId;
};

const balancesQueries = {
	listBalances: () => {
		const { merchantId } = useActiveMerchantId();

		return useInfiniteQuery<
			MerchantBalance[],
			Error,
			InfiniteData<MerchantBalance[], number>,
			(string | null)[],
			number
		>({
			queryKey: ["listBalances", merchantId],
			queryFn: () =>
				balancesProvider.listBalances(requireMerchantId(merchantId)),
			initialPageParam: 0,
			staleTime: Infinity,
			enabled: Boolean(merchantId),
			getNextPageParam: () => undefined,
			retry: 2,
		});
	},

	listWithdrawal: () => {
		const { merchantId } = useActiveMerchantId();

		return useInfiniteQuery<
			WithdrawalsPagination,
			Error,
			InfiniteData<WithdrawalsPagination, number>,
			(string | null)[],
			number
		>({
			queryKey: ["listWithdrawal", merchantId],
			queryFn: ({ pageParam }: { pageParam: number }) =>
				balancesProvider.listWithdrawals(requireMerchantId(merchantId), {
					limit: PAGE_SIZE,
					offset: pageParam,
				}),
			initialPageParam: 0,
			staleTime: Infinity,
			enabled: Boolean(merchantId),
			getNextPageParam: (lastPage) => {
				const nextOffset = lastPage.offset + lastPage.limit;
				if (nextOffset >= lastPage.totalCount) {
					return undefined;
				}
				return nextOffset;
			},
			retry: 2,
		});
	},

	listWithdrawalPaginated: (params?: ListWithdrawalsParams) => {
		const { merchantId } = useActiveMerchantId();

		return useQuery<WithdrawalsPagination, Error>({
			queryKey: ["listWithdrawalPaginated", merchantId, params],
			queryFn: () =>
				balancesProvider.listWithdrawals(requireMerchantId(merchantId), {
					...params,
					limit: params?.limit ?? 20,
					offset: params?.offset ?? 0,
				}),
			staleTime: Infinity,
			enabled: Boolean(merchantId),
			retry: 2,
		});
	},

	createWithdrawal: () => {
		const { merchantId } = useActiveMerchantId();
		const queryClient = useQueryClient();

		return useMutation({
			mutationFn: (params: {
				balanceId: string;
				recipientAddress: string;
				amount: string;
				description?: string;
				deductFeeFromAmount?: boolean;
			}) =>
				balancesProvider.createWithdrawal(
					requireMerchantId(merchantId),
					params,
				),
			onSuccess: async () => {
				await sleep(1000);
				queryClient.invalidateQueries({
					queryKey: ["listWithdrawal", merchantId],
				});
				queryClient.invalidateQueries({
					queryKey: ["listBalances", merchantId],
				});
			},
		});
	},

	cancelWithdrawal: () => {
		const { merchantId } = useActiveMerchantId();
		const queryClient = useQueryClient();

		return useMutation({
			mutationFn: (withdrawalId: string) =>
				balancesProvider.cancelWithdrawal(
					requireMerchantId(merchantId),
					withdrawalId,
				),
			onSuccess: async () => {
				await sleep(1000);
				queryClient.invalidateQueries({
					queryKey: ["listWithdrawal", merchantId],
				});
				queryClient.invalidateQueries({
					queryKey: ["listBalances", merchantId],
				});
			},
		});
	},

	getServiceFee: () => {
		const { merchantId } = useActiveMerchantId();

		return useMutation({
			mutationFn: (balanceId: string) =>
				balancesProvider.getServiceFee(
					requireMerchantId(merchantId),
					balanceId,
				),
		});
	},

	getExchangeRate: () => {
		const { merchantId } = useActiveMerchantId();

		return useMutation({
			mutationFn: (params: ConvertParams) =>
				balancesProvider.getCurrencyExchangeRate(
					requireMerchantId(merchantId),
					params,
				),
		});
	},
};

export default balancesQueries;
